import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUSES, PHOTO_TYPES, waLink, fmtDate, fmtDateTime, fmtWeight } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import PhotoImage from "@/components/PhotoImage";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ArrowLeft, MessageCircle, Phone, Mail, MapPin, Camera, Loader2, Trash2, History, StickyNote, UserPlus, Download, FileText, Pencil } from "lucide-react";
import EditJobDialog from "@/components/EditJobDialog";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusRemarks, setStatusRemarks] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("internal");
  const [addingNote, setAddingNote] = useState(false);
  const [uploadType, setUploadType] = useState("During Processing");
  const [uploadCaption, setUploadCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [assignDialog, setAssignDialog] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/jobs/${id}`);
      setData(res.data);
      setNewStatus(res.data.job.status);
    } catch {
      toast.error("Job not found");
      navigate("/jobs");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/staff").then((r) => setStaffList(r.data.filter((s) => s.active))).catch(() => {});
    }
  }, [user]);

  const photos = useMemo(() => data?.photos || [], [data]);
  const photosByType = useMemo(() => {
    const map = { All: photos };
    PHOTO_TYPES.forEach((t) => { map[t] = photos.filter((p) => p.photo_type === t); });
    return map;
  }, [photos]);

  if (!data) return <div className="p-8 text-center text-sm text-muted-foreground" data-testid="job-loading">Loading…</div>;

  const { job, customer, notes, status_history } = data;
  const isAdmin = user?.role === "admin";

  const changeStatus = async () => {
    if (!newStatus || newStatus === job.status) { setStatusDialog(false); return; }
    setSavingStatus(true);
    try {
      await api.post(`/jobs/${job.id}/status`, { new_status: newStatus, remarks: statusRemarks });
      toast.success(`Status updated to ${newStatus}`);
      setStatusDialog(false);
      setStatusRemarks("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    } finally { setSavingStatus(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await api.post(`/jobs/${job.id}/notes`, { note: noteText.trim(), note_type: noteType });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch { toast.error("Failed to add note"); } finally { setAddingNote(false); }
  };

  const uploadPhotos = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      list.forEach((f) => fd.append("files", f));
      fd.append("photo_type", uploadType);
      fd.append("caption", uploadCaption);
      await api.post(`/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${list.length} photo(s) uploaded`);
      setUploadCaption("");
      load();
    } catch { toast.error("Photo upload failed"); } finally { setUploading(false); }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/photos/${photoId}`);
      toast.success("Photo deleted");
      load();
    } catch { toast.error("Failed to delete photo"); }
  };

  const downloadPhoto = async (p) => {
    setDownloading(true);
    try {
      const res = await api.get(`/photos/${p.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${job.job_number}-${p.photo_type.replace(/\s+/g, "-")}-${p.id.slice(0, 8)}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); } finally { setDownloading(false); }
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/jobs/${job.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${job.job_number}-receipt.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); } finally { setDownloadingPdf(false); }
  };

  const assign = async () => {
    try {
      await api.post(`/jobs/${job.id}/assign`, { staff_id: assignTo || null });
      toast.success("Assignment updated");
      setAssignDialog(false);
      load();
    } catch { toast.error("Failed to assign"); }
  };

  const deleteJob = async () => {
    setDeleting(true);
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success(`Job ${job.job_number} deleted`);
      navigate("/jobs");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setDeleting(false);
    }
  };

  const photoGroups = ["All", ...PHOTO_TYPES];
  const photosOf = (t) => photosByType[t] || [];

  return (
    <div className="max-w-4xl" data-testid="job-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="job-back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="job-number">{job.job_number}</h1>
            <StatusBadge status={job.status} testId="job-status-badge" />
          </div>
          <p className="text-sm text-muted-foreground mt-1">{job.article_name} · received {fmtDateTime(job.created_at)} by {job.created_by_name}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {user?.role !== "operator" && (
            <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-job-button">
              <Pencil className="h-4 w-4 mr-1.5" /> Edit Job
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => { setAssignTo(job.assigned_staff_id || ""); setAssignDialog(true); }} data-testid="assign-job-button">
              <UserPlus className="h-4 w-4 mr-1.5" /> Assign
            </Button>
          )}
          <Button variant="outline" onClick={downloadPdf} disabled={downloadingPdf} data-testid="download-pdf-button">
            {downloadingPdf ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />} Download PDF
          </Button>
          <Button onClick={() => setStatusDialog(true)} data-testid="update-status-button" className="font-semibold">Update Status</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <Card>
          <CardContent className="p-4 sm:p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-3">Customer</h2>
            <p className="font-semibold" data-testid="job-customer-name">{customer?.customer_name}</p>
            <div className="text-sm text-muted-foreground mt-2 space-y-1.5">
              <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {customer?.mobile}</p>
              {customer?.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {customer.email}</p>}
              {(customer?.address || customer?.city) && (
                <p className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 mt-1" />
                  {[customer?.address, customer?.city, customer?.state, customer?.pincode].filter(Boolean).join(", ")}</p>
              )}
            </div>
            {waLink(customer?.whatsapp || customer?.mobile) && (
              <a href={waLink(customer?.whatsapp || customer?.mobile)} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="job-whatsapp-button"
                  className="mt-3 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-900">
                  <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp Customer
                </Button>
              </a>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 sm:p-5">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-3">Article & Finish</h2>
            <div className="text-sm space-y-1.5">
              {(job.articles || []).map((a, i) => (
                <div key={`${a.article_name}-${i}`} className={i > 0 ? "border-t pt-1.5 mt-1.5" : ""} data-testid={`job-article-${i}`}>
                  <p><span className="font-medium" data-testid={i === 0 ? "job-article-name" : undefined}>{a.article_name}</span> <span className="text-muted-foreground">× {a.quantity}</span></p>
                  <p className="text-xs text-muted-foreground">
                    {[a.category, a.base_material && `Base: ${a.base_material}`, fmtWeight(a.weight), a.condition, a.surface_finish && `${a.surface_finish} finish`].filter(Boolean).join(" · ")}
                  </p>
                  {(a.required_finishes || []).length > 0 && (
                    <p className="text-xs mt-0.5" data-testid={`job-article-finish-${i}`}>
                      <span className="text-muted-foreground">Finish:</span>{" "}
                      <span className="font-semibold text-amber-800 dark:text-amber-300">
                        {a.required_finishes.join(", ")}{a.finish_other ? ` — ${a.finish_other}` : ""}
                      </span>
                    </p>
                  )}
                </div>
              ))}
              {(job.required_finishes?.length > 0 || job.finish_other) && (
                <p className="pt-1"><span className="text-muted-foreground">Required Finish (all articles):</span>{" "}
                  <span className="font-semibold text-amber-800 dark:text-amber-300" data-testid="job-required-finish">
                    {(job.required_finishes || []).join(", ")}{job.finish_other ? ` — ${job.finish_other}` : ""}
                  </span></p>
              )}
              {job.estimated_value != null && <p><span className="text-muted-foreground">Est. Value:</span> ₹{Number(job.estimated_value).toLocaleString("en-IN")}</p>}
              {job.advance_payment != null && <p><span className="text-muted-foreground">Advance Paid:</span> ₹{Number(job.advance_payment).toLocaleString("en-IN")}</p>}
              {job.estimated_value != null && (
                <p><span className="text-muted-foreground">Balance Payment:</span>{" "}
                  <span className="font-semibold text-amber-800 dark:text-amber-300" data-testid="job-balance-payment">₹{Number(job.estimated_value - (job.advance_payment || 0)).toLocaleString("en-IN")}</span></p>
              )}
              {job.estimated_value != null && (
                <p className="text-xs text-muted-foreground" data-testid="job-tax-note">All amounts are exclusive of tax. Applicable tax will be charged additionally.</p>
              )}
              {job.expected_completion_date && <p><span className="text-muted-foreground">Expected by:</span> {fmtDate(job.expected_completion_date)}</p>}
              {job.completed_at && <p><span className="text-muted-foreground">Completed:</span> {fmtDateTime(job.completed_at)}</p>}
              <p><span className="text-muted-foreground">Assigned to:</span> {data.assigned_staff?.name || "Unassigned"}</p>
              {job.special_instructions && <p className="pt-1 text-xs border-t mt-2"><span className="font-semibold">Customer instructions:</span> {job.special_instructions}</p>}
              {job.staff_remarks && <p className="text-xs"><span className="font-semibold">Staff remarks:</span> {job.staff_remarks}</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Photos */}
      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Photos ({photos.length})</h2>
          </div>
          <div className="flex flex-wrap items-end gap-2 rounded-xl border border-dashed p-3 mb-4" data-testid="photo-upload-zone">
            <div>
              <Label className="text-xs">Photo Type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger data-testid="photo-type-select" className="h-10 w-44 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{PHOTO_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs">Caption (optional)</Label>
              <Input data-testid="photo-caption-input" value={uploadCaption} onChange={(e) => setUploadCaption(e.target.value)} className="h-10 mt-1" placeholder="e.g. Dent on lid" />
            </div>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="job-camera-input" onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} />
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="job-gallery-input" onChange={(e) => { uploadPhotos(e.target.files); e.target.value = ""; }} />
            <Button variant="secondary" onClick={() => camRef.current?.click()} disabled={uploading} data-testid="job-take-photo-button">
              <Camera className="h-4 w-4 mr-1.5" /> Camera
            </Button>
            <Button variant="secondary" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="job-upload-photo-button">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
            </Button>
          </div>

          <Tabs defaultValue="All">
            <TabsList className="flex-wrap h-auto" data-testid="photo-tabs">
              {photoGroups.map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`photo-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                  {t} {t !== "All" ? `(${photosOf(t).length})` : ""}
                </TabsTrigger>
              ))}
            </TabsList>
            {photoGroups.map((t) => (
              <TabsContent key={t} value={t}>
                {photosOf(t).length === 0 ? (
                  <p className="py-8 text-center text-sm text-muted-foreground">No {t === "All" ? "" : t.toLowerCase()} photos yet.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-3" data-testid={`photo-gallery-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                    {photosOf(t).map((p) => (
                      <div key={p.id} className="group relative rounded-xl overflow-hidden border bg-muted cursor-zoom-in" data-testid={`photo-${p.id}`} onClick={() => setLightboxPhoto(p)} role="button" tabIndex={0}>
                        <PhotoImage photoId={p.id} className="w-full aspect-square object-cover" alt={p.caption || p.photo_type} />
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                          <p className="text-[10px] font-semibold text-white">{p.photo_type}</p>
                          {p.caption && <p className="text-[10px] text-white/80 truncate">{p.caption}</p>}
                          <p className="text-[9px] text-white/60">{p.uploaded_by_name} · {fmtDateTime(p.created_at)}</p>
                        </div>
                        {(isAdmin || p.uploaded_by === user?.user_id) && (
                          <button onClick={(e) => { e.stopPropagation(); deletePhoto(p.id); }} data-testid={`photo-delete-${p.id}`}
                            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-3 flex items-center gap-1.5">
            <StickyNote className="h-3.5 w-3.5" /> Notes
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <Select value={noteType} onValueChange={setNoteType}>
              <SelectTrigger data-testid="note-type-select" className="sm:w-48 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer Note</SelectItem>
                <SelectItem value="internal">Internal Staff Note</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note…" className="h-11 flex-1" onKeyDown={(e) => e.key === "Enter" && addNote()} />
            <Button onClick={addNote} disabled={addingNote || !noteText.trim()} data-testid="add-note-button" className="h-11">
              {addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Note"}
            </Button>
          </div>
          <div className="space-y-2" data-testid="notes-list">
            {notes.length === 0 && <p className="text-sm text-muted-foreground">No notes yet.</p>}
            {notes.map((n) => (
              <div key={n.id} className="rounded-lg border p-3" data-testid={`note-${n.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`status-pill ${n.note_type === "customer" ? "bg-sky-50 text-sky-800 border-sky-200 dark:bg-sky-950/60 dark:text-sky-300 dark:border-sky-900" : "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700"}`}>
                    {n.note_type === "customer" ? "Customer" : "Internal"}
                  </span>
                  <span className="text-xs text-muted-foreground">{n.created_by_name} · {fmtDateTime(n.created_at)}</span>
                </div>
                <p className="text-sm mt-1.5">{n.note}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-3 flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" /> Status History
          </h2>
          <div className="space-y-0" data-testid="status-history-list">
            {status_history.map((h, i) => (
              <div key={h.id} className="flex gap-3 pb-4 relative" data-testid={`history-${h.id}`}>
                {i < status_history.length - 1 && <div className="absolute left-[7px] top-5 bottom-0 w-px bg-border" />}
                <div className="h-4 w-4 rounded-full border-2 border-primary bg-background mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium">
                    {h.old_status ? <>{h.old_status} → </> : ""}<span className="text-amber-800 dark:text-amber-300">{h.new_status}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{h.changed_by_name} · {fmtDateTime(h.created_at)}{h.remarks ? ` · ${h.remarks}` : ""}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {isAdmin && (
        <div className="mt-6 border-t pt-4">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" data-testid="delete-job-button"><Trash2 className="h-4 w-4 mr-1.5" /> Delete Job</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete job {job.job_number}?</AlertDialogTitle>
                <AlertDialogDescription>This permanently deletes the job, its notes and history. Photos will be removed from view. This cannot be undone.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-job-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteJob} disabled={deleting} data-testid="delete-job-confirm" className="bg-destructive text-destructive-foreground">
                  {deleting ? "Deleting…" : "Delete Permanently"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      <EditJobDialog job={job} open={editOpen} onOpenChange={setEditOpen} onSaved={load} />

      {/* Photo lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={(o) => !o && setLightboxPhoto(null)}>
        <DialogContent className="sm:max-w-3xl p-3" data-testid="photo-lightbox" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Photo preview</DialogTitle>
          {lightboxPhoto && (
            <div>
              <PhotoImage photoId={lightboxPhoto.id} className="w-full max-h-[70vh] object-contain rounded-lg bg-black/5" alt={lightboxPhoto.caption || lightboxPhoto.photo_type} testId="lightbox-image" />
              <div className="flex items-center justify-between gap-3 mt-3 px-1">
                <div className="text-sm min-w-0">
                  <p className="font-semibold truncate" data-testid="lightbox-caption">{lightboxPhoto.photo_type}{lightboxPhoto.caption ? ` — ${lightboxPhoto.caption}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{lightboxPhoto.uploaded_by_name} · {fmtDateTime(lightboxPhoto.created_at)}</p>
                </div>
                <Button onClick={() => downloadPhoto(lightboxPhoto)} disabled={downloading} data-testid="photo-download-button" className="shrink-0">
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> Download</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent data-testid="status-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Update Job Status</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Current: <StatusBadge status={job.status} /></p>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="new-status-select" className="h-11"><SelectValue placeholder="New status" /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea data-testid="status-remarks-input" value={statusRemarks} onChange={(e) => setStatusRemarks(e.target.value)} placeholder="Remarks (optional)" rows={2} />
          </div>
          <DialogFooter>
            <Button onClick={changeStatus} disabled={savingStatus || newStatus === job.status} data-testid="confirm-status-button" className="w-full sm:w-auto">
              {savingStatus ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Status"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent data-testid="assign-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Assign Job</DialogTitle></DialogHeader>
          <Select value={assignTo} onValueChange={setAssignTo}>
            <SelectTrigger data-testid="assign-staff-select" className="h-11"><SelectValue placeholder="Select staff member" /></SelectTrigger>
            <SelectContent>
              {staffList.map((s) => <SelectItem key={s.user_id} value={s.user_id} data-testid={`assign-option-${s.user_id}`}>{s.name} ({s.role})</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button onClick={assign} data-testid="confirm-assign-button" className="w-full sm:w-auto">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
